import { useParams, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { RoleBadge } from '@/components/team/role-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency, formatRelative } from '@/lib/utils';

export function TeamMemberPage() {
  const { memberId } = useParams({ strict: false }) as { memberId: string };

  // Placeholder - will be wired to trpc.team.getById
  const isLoading = false;
  const member = null as any;
  void memberId; // used in query

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!member) {
    return (
      <div className="space-y-6">
        <Link to="/team" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to team
        </Link>
        <div className="py-12 text-center text-muted-foreground">Team member not found.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/team" className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <Avatar className="h-12 w-12">
          <AvatarImage src={member.user?.avatarUrl ?? undefined} />
          <AvatarFallback>
            {member.user?.name
              ?.split(' ')
              .map((n: string) => n[0])
              .join('')
              .toUpperCase() ?? '?'}
          </AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{member.user?.name}</h1>
          <div className="flex items-center gap-2">
            <RoleBadge role={member.role} />
            <span className="text-sm text-muted-foreground">{member.user?.email}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Revenue Sourced
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatCurrency(member.revenueSourced ?? 0, 'CAD')}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Deals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{member.dealCount ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Commission (QTD)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {formatCurrency(member.commission ?? 0, 'CAD')}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Deals</CardTitle>
        </CardHeader>
        <CardContent>
          {(!member.deals || member.deals.length === 0) ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No deals yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Deal</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {member.deals.map((deal: any) => (
                  <TableRow key={deal.id}>
                    <TableCell>
                      <Link
                        to="/deals/$dealId"
                        params={{ dealId: deal.id }}
                        className="font-medium text-primary hover:underline"
                      >
                        {deal.title}
                      </Link>
                    </TableCell>
                    <TableCell>{deal.stage}</TableCell>
                    <TableCell className="text-right">
                      {deal.amount ? formatCurrency(deal.amount, deal.currency ?? 'CAD') : '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatRelative(deal.updatedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
